'use client';
import { useRouter as useCompatRouter } from 'next/compat/router';
import {
  useRouter as useAppRouter,
  usePathname,
  useSearchParams,
} from 'next/navigation';

/**
 * useUniversalRouter
 *
 * Provides a router object that works in both the classic Pages Router and the
 * new App Router without the calling component needing to know which runtime
 * it is in.
 *
 * Only the properties that are currently required by components have been
 * included. If you need additional fields in the future just extend the return
 * object in the App-Router branch.
 */
export function useUniversalRouter() {
  // Classic router – returns null inside the App Router.
  const pagesRouter = useCompatRouter();

  // App-router hooks (always defined inside <Client> components).
  const appRouter = useAppRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // If the classic router exists we are executing under the Pages Router – just
  // return it unchanged so callers get the full feature set.
  if (pagesRouter) {
    return pagesRouter;
  }

  // Otherwise we are executing under the App Router – compose the minimal
  // object expected by the rest of the code-base.
  const queryObj = {};
  if (searchParams) {
    searchParams.forEach((v, k) => {
      queryObj[k] = v;
    });
  }

  const href = pathname || '';
  const search = searchParams?.toString() || '';

  return {
    // Match a subset of the Pages Router API that existing components rely on.
    asPath: search ? `${href}?${search}` : href,
    pathname: href,
    query: queryObj,
    isReady: true,
    push: appRouter?.push ?? (() => {}),
    replace: appRouter?.replace ?? (() => {}),
    back: appRouter?.back ?? (() => {}),
  };
}
